import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { useDemo } from '../../contexts/DemoContext';
import { useLocation } from 'react-router-dom';
import { Z_INDEX } from '../../constants';
import { useTheme } from '../../contexts/ThemeContext';

// Spotlight should be above modal but below toast
const OVERLAY_Z = Z_INDEX.modal + 50;
const SPOTLIGHT_Z = OVERLAY_Z + 1;

function scrollRowIntoViewWithExtraBottom(rowEl, padding = 120) {
  if (!rowEl) return;
  rowEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

  setTimeout(() => {
    // Find the nearest scrollable ancestor (the subpage content container)
    let container = rowEl.parentElement;
    while (container && container !== document.body && container.scrollHeight <= container.clientHeight) {
      container = container.parentElement;
    }
    if (!container || container === document.body) return;

    const rowRect = rowEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    if (rowRect.bottom > containerRect.bottom - padding) {
      const extra = rowRect.bottom - (containerRect.bottom - padding);
      container.scrollTop += extra;
    }
  }, 350);
}

// Four overlay blocks that "frame" the spotlight
const OverlayBlock = styled.div`
  position: fixed !important;
  background: rgba(0,0,0,0.18);
  /* Never intercept clicks; let AppLayout gating + target handlers decide */
  pointer-events: none !important;
  /* avoid the "blocked" cursor since we're not blocking anymore */
  cursor: default;
  z-index: ${OVERLAY_Z} !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const Spotlight = styled.div`
  position: fixed !important;
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  top: ${(props) => props.$top}px;
  left: ${(props) => props.$left}px;
  pointer-events: none;
  z-index: ${SPOTLIGHT_Z} !important;
  opacity: ${(props) => (props.$isActive ? 1 : 0)};
  box-shadow:
    0 0 0 4px ${({ $isDarkMode }) => $isDarkMode ? '#31ff79' : '#316bff'},
    0 0 18px 6px ${({ $isDarkMode }) => $isDarkMode ? '#32ff85AA' : '#316bff55'},
    0 0 40px 16px ${({ $isDarkMode }) => $isDarkMode ? '#2dff78AA' : '#295bff33'};
  border-radius: 8px;
  border: 2px solid ${({ $isDarkMode }) => $isDarkMode ? '#31ff79' : '#316bff'};
  animation: ${({ $isDarkMode }) => $isDarkMode ? 'spotlight-green-pulse' : 'spotlight-blue-pulse'} 1.1s infinite alternate;

  @keyframes spotlight-green-pulse {
    0%   { box-shadow: 0 0 0 4px #31ff79, 0 0 16px 4px #31ff79AA, 0 0 40px 8px #31ff7966; }
    100% { box-shadow: 0 0 0 8px #89ffc7, 0 0 32px 14px #5affa566, 0 0 50px 18px #22dd6788; }
  }
  @keyframes spotlight-blue-pulse {
    0%   { box-shadow: 0 0 0 4px #316bff, 0 0 16px 4px #316bff66, 0 0 40px 8px #316bff44; }
    100% { box-shadow: 0 0 0 8px #aacfff, 0 0 32px 14px #91b9ff55, 0 0 50px 18px #8caaff44; }
  }
`;

const ConnectingLine = styled.div`
  position: fixed !important;
  width: ${props => props.$isVertical ? '3px' : props.$width + 'px'};
  height: ${props => props.$isVertical ? props.$height + 'px' : '3px'};
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  background: linear-gradient(
    ${props => props.$isVertical ? '180deg' : '90deg'},
    ${({ $isDarkMode }) => $isDarkMode ? '#19fa6c 0%, #2eebd3 100%' : '#3c6fff 0%, #91b9ff 100%'}
  );
  box-shadow: 0 0 10px 1px ${({ $isDarkMode }) => $isDarkMode ? '#19fa6c80' : '#316bff44'};
  z-index: ${SPOTLIGHT_Z + 1} !important;
  opacity: ${props => props.$isActive ? 1 : 0};
  pointer-events: none;
`;

const GlowingDot = styled.div`
  position: fixed !important;
  width: 14px;
  height: 14px;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  background: ${({ $isDarkMode }) => $isDarkMode ? '#19fa6c' : '#316bff'};
  border-radius: 50%;
  z-index: ${SPOTLIGHT_Z + 3} !important; // Increased to always be on top
  opacity: ${props => props.$isActive ? 1 : 0};
  box-shadow: 0 0 10px 4px ${({ $isDarkMode }) => $isDarkMode ? '#19fa6c88' : '#aacfff88'};
  pointer-events: none;
  animation: ${({ $isDarkMode }) => $isDarkMode ? 'breathe-green' : 'breathe-blue'} 3s infinite ease-in-out;
  
  @keyframes breathe-green {
    0% { transform: scale(1); box-shadow: 0 0 10px 4px #19fa6c88; }
    50% { transform: scale(1.2); box-shadow: 0 0 15px 6px #19fa6c88; }
    100% { transform: scale(1); box-shadow: 0 0 10px 4px #19fa6c88; }
  }
  
  @keyframes breathe-blue {
    0% { transform: scale(1); box-shadow: 0 0 10px 4px #aacfff88; }
    50% { transform: scale(1.2); box-shadow: 0 0 15px 6px #aacfff88; }
    100% { transform: scale(1); box-shadow: 0 0 10px 4px #aacfff88; }
  }
`;

const Label = styled.div`
  position: fixed !important;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  background: ${({ $isDarkMode }) =>
    $isDarkMode
      ? 'linear-gradient(130deg, #212c23 60%, #17453c 100%)'
      : 'linear-gradient(130deg, #f8fafd 60%, #c5d7f8 100%)'};
  color: ${({ $isDarkMode }) => ($isDarkMode ? '#e3ffe9' : '#1d2b41')};
  padding: 10px 14px;
  border-radius: 8px;
  box-shadow: ${({ $isDarkMode }) =>
    $isDarkMode
      ? '0 4px 32px 0 #19fa6c38, 0 2px 12px rgba(0,0,0,0.25)'
      : '0 4px 24px 0 #2353ff28, 0 2px 12px rgba(0,0,0,0.12)'};
  font-size: 0.72rem;
  line-height: 1.5;
  max-width: 188px;
  min-width: 135px;
  pointer-events: none;
  z-index: ${SPOTLIGHT_Z + 2} !important;
  opacity: ${props => props.$isActive ? 1 : 0};
  transition: opacity 0.3s ease;
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    border: 1px solid ${({ $isDarkMode }) => $isDarkMode ? '#19fa6c40' : '#316bff40'};
    pointer-events: none;
  }
`;

const Title = styled.div`
  color: ${({ $isDarkMode }) => $isDarkMode ? '#19fa6c' : '#316bff'};
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 0.28em;
  letter-spacing: 0.01em;
`;

const CloseButton = styled.button`
  position: fixed !important;
  top: ${props => props.$top - 12}px;
  left: ${props => props.$left + props.$width - 12}px;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${({ $isDarkMode }) => $isDarkMode ? '#2a2a2a' : '#f0f4f8'};
  color: ${({ $isDarkMode }) => $isDarkMode ? '#19fa6c' : '#316bff'};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: ${SPOTLIGHT_Z + 2} !important;
  box-shadow: ${({ $isDarkMode }) =>
    $isDarkMode
      ? '0 0 0 2px #19fa6c, 0 0 10px 2px #19fa6c80'
      : '0 0 0 2px #316bff, 0 0 10px 2px #316bff44'};
  transition: all 0.2s ease;
  &:hover {
    background: ${({ $isDarkMode }) => $isDarkMode ? '#3a3a3a' : '#ffffff'};
    transform: scale(1.1);
    box-shadow: ${({ $isDarkMode }) =>
    $isDarkMode
      ? '0 0 0 2px #19fa6c, 0 0 15px 4px #19fa6c80'
      : '0 0 0 2px #316bff, 0 0 15px 4px #316bff66'};
  }
  &:before {
    content: "×";
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    line-height: 1;
  }
  &:after {
    content: "Close";
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    background: ${({ $isDarkMode }) =>
    $isDarkMode
      ? 'rgba(25, 250, 108, 0.1)'
      : 'rgba(49, 107, 255, 0.1)'};
    color: ${({ $isDarkMode }) => $isDarkMode ? '#19fa6c' : '#316bff'};
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 10px;
    white-space: nowrap;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  &:hover:after {
    opacity: 1;
  }
`;

const SpotlightOverlay = () => {
  const { isConfirmingStop, onEffectComplete, activeSpotlight, onAction } = useDemo();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const [spotlightState, setSpotlightState] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [showLabel, setShowLabel] = useState(true);

  // Helper function to wait for stable rect before showing spotlight
  function waitForStableRect(rowEl, callback) {
    let lastRect = null;
    let stableFrames = 0;
    let tries = 0;
    const maxTries = 60; // Increased to 3 seconds max (60 * 50ms)
    const requiredStableFrames = 1; // Reduced for instant spotlight appearance

    function check() {
      const rect = rowEl.getBoundingClientRect();
      const hasValidSize = rect.width > 0 && rect.height > 0;
      const hasValidPosition = rect.top !== 0 || rect.left !== 0;
      const isStable = hasValidSize && hasValidPosition && (!lastRect ||
        (Math.abs(rect.top - lastRect.top) < 1 &&
         Math.abs(rect.left - lastRect.left) < 1 &&
         Math.abs(rect.width - lastRect.width) < 1 &&
         Math.abs(rect.height - lastRect.height) < 1));

      if (isStable) {
        stableFrames++;
      } else {
        stableFrames = 0;
      }
      lastRect = rect;

      if (stableFrames >= requiredStableFrames) {
        callback(rect);
      } else if (tries++ < maxTries) {
        setTimeout(check, 20); // Faster checks for more responsive spotlights
      } else {
        // If we timeout but have a valid rect, use it anyway
        if (hasValidSize && hasValidPosition) {
          console.warn('Spotlight: Using best effort rect after timeout');
          callback(rect);
        } else {
          console.warn('Spotlight: Failed to get stable rect after timeout');
        }
      }
    }
    check();
  }

  // Add debounced update function
  const debouncedUpdate = useRef(null);

  // Helper function to check if element is in viewport
  const isElementInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };
  
  const updatePosition = useCallback(() => {
    if (!spotlightState?.target) return;
    
    let el = spotlightState.target;
    if (el.id) {
      const live = document.getElementById(el.id);
      if (live) el = live;
    }
    
    const rect = el.getBoundingClientRect();
    if (
      rect.width > 0 && 
      rect.height > 0 && 
      (rect.top !== 0 || rect.left !== 0)
    ) {
      const newRect = {
        width: rect.width + 16,
        height: rect.height + 16,
        top: rect.top - 8,
        left: rect.left - 8,
      };
      
      // Only update if position changed significantly (more than 2px) to avoid micro-adjustments
      if (spotlightState.rect) {
        const topDiff = Math.abs(newRect.top - spotlightState.rect.top);
        const leftDiff = Math.abs(newRect.left - spotlightState.rect.left);
        if (topDiff < 2 && leftDiff < 2) {
          return; // Skip update if change is too small
        }
      }
      
      setSpotlightState(prev => ({
        ...prev,
        rect: newRect
      }));
    }
  }, [spotlightState?.target, spotlightState?.rect]);

  // Track when spotlight first became active to prevent immediate updates
  const spotlightActivatedAt = useRef(null);

  // Enhanced position monitoring
  useEffect(() => {
    if (!spotlightState?.target || !isActive) {
      spotlightActivatedAt.current = null;
      return;
    }

    // Record when spotlight first became active
    if (!spotlightActivatedAt.current) {
      spotlightActivatedAt.current = Date.now();
    }

    // Clear any existing debounced update
    if (debouncedUpdate.current) {
      clearTimeout(debouncedUpdate.current);
    }

    const handleUpdate = () => {
      // Don't update position for 500ms after spotlight first appears
      if (spotlightActivatedAt.current && Date.now() - spotlightActivatedAt.current < 500) {
        return;
      }
      
      if (debouncedUpdate.current) {
        clearTimeout(debouncedUpdate.current);
      }
      debouncedUpdate.current = setTimeout(updatePosition, 100);
    };

    // Monitor for DOM mutations affecting layout
    const observer = new MutationObserver(handleUpdate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Monitor scroll and resize events
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    // Check position periodically for any missed changes
    const intervalCheck = setInterval(handleUpdate, 500);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      clearInterval(intervalCheck);
      if (debouncedUpdate.current) {
        clearTimeout(debouncedUpdate.current);
      }
    };
  }, [spotlightState?.target, isActive, updatePosition]);

  // Block clicks on non-spotlight elements while a spotlight is active
  useEffect(() => {
    if (!spotlightState?.target) return;

    const allowClick = (el) => {
      if (!el) return false;
      if (el === spotlightState.target) return true;
      if (el.closest('[data-allow-demo-click="true"]')) return true;
      if (spotlightState.target.contains(el)) return true;
      return false;
    };

    const handleClickCapture = (e) => {
      if (!spotlightState?.target) return;
      if (allowClick(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('click', handleClickCapture, true);
    return () => document.removeEventListener('click', handleClickCapture, true);
  }, [spotlightState?.target]);

  // CLEANUP SPOTLIGHT STATE ON CONFIRMING STOP
  useEffect(() => {
    if (isConfirmingStop) {
      setSpotlightState(null);
      setIsActive(false);
    }
  }, [isConfirmingStop]);

  // Determine if we're in a scrollable research view
  const isScrollableResearch = useMemo(() => {
    if (!spotlightState?.target) return false;

    const researchModalTypes = [
      'SALES_REMINDER_STOCK_OPTIONS',
      'SALES_REMINDER_ALTERNATIVES',
      'SALES_REMINDER_ESTATE_PLANNING'
    ];

    const targetId = spotlightState.target.id || '';
    const isAddReminderButton = targetId.startsWith('modal-add-reminder-btn-');
    const modalType = isAddReminderButton
      ? targetId.split('modal-add-reminder-btn-')[1]
      : '';

    return isAddReminderButton && researchModalTypes.includes(modalType);
  }, [spotlightState?.target]);

  // **NEW**: compute modalType so we can disable its Dismiss/Open buttons
  const modalType = useMemo(() => {
    const id = spotlightState?.target?.id || '';
    return id.startsWith('modal-add-reminder-btn-')
      ? id.split('modal-add-reminder-btn-')[1]
      : null;
  }, [spotlightState]);

  // **NEW**: in Phase 3, disable pointer-events on dismiss/open
  useEffect(() => {
    if (!isScrollableResearch || !modalType) return;
    const dismissBtn = document.querySelector(`#modal-dismiss-btn-${modalType}`);
    const openBtn = document.querySelector(`#modal-open-new-window-btn-${modalType}`);
    if (dismissBtn) dismissBtn.style.pointerEvents = 'none';
    if (openBtn) openBtn.style.pointerEvents = 'none';

    return () => {
      if (dismissBtn) dismissBtn.style.pointerEvents = '';
      if (openBtn) openBtn.style.pointerEvents = '';
    };
  }, [isScrollableResearch, modalType]);

  // Listen for custom spotlight events with enhanced error handling
  useEffect(() => {
    const handleSpotlight = (event) => {
      try {
        const { detail } = event;
        if (!detail?.target) {
          setSpotlightState(null);
          setIsActive(false);
          return;
        }

        // Add data-allow-demo-click attribute to the target
        detail.target.setAttribute("data-allow-demo-click", "true");

        // Fast-path for Tax Analysis Section (instant spotlight)
        if (detail.target.id === 'tax-analysis-section-spotlight' || 
            detail.target.id === 'tax-analysis-section-button' ||
            detail.target.getAttribute('data-spotlight-id') === 'tax-analysis') {
          const rect = detail.target.getBoundingClientRect();
          setSpotlightState({
            ...detail,
            rect: {
              width: rect.width + 16,
              height: rect.height + 16,
              top: rect.top - 8,
              left: rect.left - 8,
            }
          });
          setIsActive(true);
          return;
        }

        const isFocusRow = detail.target?.dataset?.spotlightTarget?.startsWith('holding-') && 
                          detail.target.tagName === "TR";

        const updateSpotlightState = (target, skipWait = false) => {
          const isButtonLike = target.tagName === 'BUTTON' ||
            target.getAttribute('role') === 'button' ||
            target.closest('button') ||
            target.classList.contains('btn') ||
            target.classList.contains('button');
          const padding = isButtonLike ? 12 : 16;
          const offset = padding / 2;

          if (skipWait) {
            // If the element is ready, show instantly!
            const rect = target.getBoundingClientRect();
            setSpotlightState({
              ...detail,
              rect: {
                width: rect.width + padding,
                height: rect.height + padding,
                top: rect.top - offset,
                left: rect.left - offset,
              }
            });
            setIsActive(true);
            // Re-validate position shortly after initial set
            setTimeout(updatePosition, 20);
            return;
          }

          // Else: robust waiting for animations/layout
          waitForStableRect(target, (rect) => {
            setSpotlightState({
              ...detail,
              rect: {
                width: rect.width + padding,
                height: rect.height + padding,
                top: rect.top - offset,
                left: rect.left - offset,
              }
            });
            setIsActive(true);
            // Re-validate after first paint
            setTimeout(updatePosition, 20);
          });
        };

        // Fast-path: check if element is already in view and has size
        const rect = detail.target.getBoundingClientRect();
        const isLargeEnough = rect.width > 10 && rect.height > 10;
        const isInView = isElementInViewport(detail.target);
        
        // Additional check for modal/portal content
        const isInModal = !!(detail.target.closest('[role="dialog"], .Modal, .modal, [data-demo-modal], .MeetingBriefingRoomModal, .DemoSelectionModal, .DemoModal'));

        if (isFocusRow) {
          scrollRowIntoViewWithExtraBottom(detail.target, 160);
          setTimeout(() => updateSpotlightState(detail.target), 400);
        } else if (!isInView) {
          detail.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          // Wait until it's visible, then check size
          let tries = 0;
          function pollUntilVisible() {
            const r = detail.target.getBoundingClientRect();
            if (isElementInViewport(detail.target) && r.width > 10 && r.height > 10) {
              updateSpotlightState(detail.target);
            } else if (++tries < 30) { // Try for up to 1.2s
              setTimeout(pollUntilVisible, 40);
            } else {
              updateSpotlightState(detail.target); // Fallback after timeout
            }
          }
          pollUntilVisible();
        } else if (isLargeEnough && !isInModal) {
          // Static section that's visible and not in a modal—show spotlight right away
          updateSpotlightState(detail.target, true);
        } else if (isInModal) {
          // Modal buttons: wait longer for modal layout/ fonts to settle, then measure once
          setTimeout(() => updateSpotlightState(detail.target), 200);
        } else {
          // In view but may be animating, wait for stability
          updateSpotlightState(detail.target);
        }
      } catch (error) {
        console.error('Error handling spotlight event:', error);
      }
    };

    window.addEventListener("spotlight", handleSpotlight);
    return () => window.removeEventListener("spotlight", handleSpotlight);
  }, []);

  // Clean up overlay after clicking the spotlighted element
  useEffect(() => {
    if (!spotlightState?.target) return;
    if (spotlightState.showCloseButton) return;

    // Add data-spotlight-active attribute
    spotlightState.target.setAttribute('data-spotlight-active', 'true');

    const handleClick = (e) => {
      // Check for any open modals using multiple selectors
      const modalIsOpen = document.querySelector(
        '.Modal, .modal, [role="dialog"], [data-demo-modal], .MeetingBriefingRoomModal, .DemoSelectionModal, .DemoModal'
      );
      const clickedInsideSpotlight = spotlightState?.target?.contains?.(e.target);
      if (modalIsOpen && !clickedInsideSpotlight) {
        console.log('⛔ Spotlight click ignored: modal open and not on spotlighted element', {
          modalClass: modalIsOpen.className,
          modalRole: modalIsOpen.getAttribute('role'),
          modalType: modalIsOpen.getAttribute('data-demo-modal'),
          clickTarget: e.target
        });
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Clean up data-allow-demo-click and data-spotlight-active attributes
      if (spotlightState?.target?.removeAttribute) {
        spotlightState.target.removeAttribute("data-allow-demo-click");
        spotlightState.target.removeAttribute("data-spotlight-active");
      }

      setSpotlightState(null);
      setIsActive(false);

      if (spotlightState.onClose) {
        spotlightState.onClose();
      } else if (activeSpotlight?.onClose) {
        activeSpotlight.onClose();
      } else if (!spotlightState.disableAutoAdvance) {
        onAction?.({ type: 'ADVANCE_DEMO' });
        onEffectComplete?.();
      }
    };
    spotlightState.target.addEventListener('click', handleClick);
    return () => {
      // Clean up event listener and attributes
      spotlightState.target.removeEventListener('click', handleClick);
      if (spotlightState?.target?.removeAttribute) {
        spotlightState.target.removeAttribute("data-allow-demo-click");
        spotlightState.target.removeAttribute("data-spotlight-active");
      }
    };
  }, [spotlightState, activeSpotlight, onAction, onEffectComplete]);

  // Hide the overlay when the route changes!
  useEffect(() => {
    setSpotlightState(null);
    setIsActive(false);
    setShowLabel(true);  // Reset label visibility when route changes
  }, [location.pathname]);

  // Early return if confirming stop
  if (isConfirmingStop) return null;

  if (!spotlightState || !isActive) return null;

  // Calculate window size and spotlight rect
  const { width, height, top, left } = spotlightState.rect;
  const winW = window.innerWidth;
  const winH = window.innerHeight;

  // Calculate positions...
  const spotlightCenterY = top + (height / 2);
  const labelHeight = 80;     // Was 120
  const labelWidth = 170;     // Was 255
  const minMargin = 14;       // Was 20

  // Check if target is in a modal
  const isInModal = !!(spotlightState.target.closest('[role="dialog"], .Modal, .modal, [data-demo-modal], .MeetingBriefingRoomModal, .DemoSelectionModal, .DemoModal'));

  // Adjust sidebar width and margins for modals
  const sidebarWidth = isInModal ? 0 : 380; // Fixed width for non-modal, 0 for modal
  const effectiveMinMargin = isInModal ? 12 : minMargin;

  const dotSize = 14;
  const minSpaceForHorizontal = labelWidth + 54; // Was +80

  const effectiveWindowWidth = winW - sidebarWidth;
  const spaceOnLeft = left - effectiveMinMargin;
  const spaceOnRight = effectiveWindowWidth - (left + width) - effectiveMinMargin;
  const spaceBelow = winH - (top + height) - effectiveMinMargin;
  const spaceAbove = top - effectiveMinMargin;

  // For modals, prefer vertical positioning
  let positionMode = 'below';
  if (isInModal) {
    positionMode = spaceBelow >= labelHeight + 24 ? 'below' :
      spaceAbove >= labelHeight + 24 ? 'above' :
        spaceOnRight > spaceOnLeft ? 'right' : 'left';
  } else {
    const hasSpaceLeft = spaceOnLeft >= minSpaceForHorizontal;
    const hasSpaceRight = spaceOnRight >= minSpaceForHorizontal;
    const preferRight = spaceOnRight > spaceOnLeft;

    if (!hasSpaceLeft && !hasSpaceRight) {
      positionMode = spaceBelow >= labelHeight + 40 ? 'below' : 'above';
    } else if (preferRight && hasSpaceRight) {
      positionMode = 'right';
    } else if (hasSpaceLeft) {
      positionMode = 'left';
    }
  }

  let labelTop, labelLeft, dotLeft, dotTop, lineLeft, lineTop, lineWidth, lineHeight;
  let isVertical = false;

  switch (positionMode) {
    case 'left':
      // Position label with enough space for line
      labelLeft = Math.max(effectiveMinMargin, left - labelWidth - 54);
      labelTop = spotlightCenterY - (labelHeight / 2);

      // Dot on left edge of target
      dotLeft = left - (dotSize / 2);
      dotTop = spotlightCenterY - (dotSize / 2);

      // Line connects label to dot
      lineLeft = labelLeft + labelWidth;
      lineTop = spotlightCenterY - 1.5;
      lineWidth = left - (labelLeft + labelWidth) + (dotSize / 2);
      lineHeight = 3;
      isVertical = false;
      break;
    case 'right':
      // Position label with enough space for line
      labelLeft = left + width + 54;
      labelTop = spotlightCenterY - (labelHeight / 2);

      // Dot on right edge of target
      dotLeft = left + width - (dotSize / 2);
      dotTop = spotlightCenterY - (dotSize / 2);

      // Line connects dot to label
      lineLeft = left + width;
      lineTop = spotlightCenterY - 1.5;
      lineWidth = labelLeft - (left + width);
      lineHeight = 3;
      isVertical = false;
      break;
    case 'below':
      // Center label exactly with target if possible
      labelLeft = Math.max(
        effectiveMinMargin,
        Math.min(
          left + (width / 2) - (labelWidth / 2),
          effectiveWindowWidth - labelWidth - effectiveMinMargin
        )
      );
      labelTop = top + height + 24; // Reduced from 36 for tighter connection

      // Dot centered on target's bottom edge
      dotLeft = left + (width / 2) - (dotSize / 2);
      dotTop = top + height - (dotSize / 2);

      // Line connects dot center to label
      lineLeft = left + (width / 2) - 1.5;
      lineTop = top + height;
      lineWidth = 3;
      lineHeight = (labelTop - lineTop) + 8; // Extend slightly into label
      isVertical = true;
      break;

    case 'above':
      // Center label exactly with target if possible
      labelLeft = Math.max(
        effectiveMinMargin,
        Math.min(
          left + (width / 2) - (labelWidth / 2),
          effectiveWindowWidth - labelWidth - effectiveMinMargin
        )
      );
      labelTop = Math.max(effectiveMinMargin, top - labelHeight - 24);

      // Dot centered on target's top edge
      dotLeft = left + (width / 2) - (dotSize / 2);
      dotTop = top - (dotSize / 2);

      // Line connects label to dot center
      lineLeft = left + (width / 2) - 1.5;
      lineTop = labelTop + labelHeight - 8; // Start slightly inside label
      lineWidth = 3;
      lineHeight = top - (labelTop + labelHeight) + 16; // Account for dot overlap
      isVertical = true;
      break;
  }

  return createPortal(
    <>
      {/* Top block */}
      <OverlayBlock $allowScroll={isScrollableResearch}
        style={{ top: 0, left: 0, width: '100vw', height: top }} />

      {/* Left block */}
      <OverlayBlock $allowScroll={isScrollableResearch}
        style={{ top, left: 0, width: left, height }} />

      {/* Right block */}
      <OverlayBlock $allowScroll={isScrollableResearch}
        style={{ top, left: left + width, width: winW - (left + width), height }} />

      {/* Bottom block */}
      <OverlayBlock $allowScroll={isScrollableResearch}
        style={{ top: top + height, left: 0, width: '100vw', height: winH - (top + height) }} />

      {/* Glowing border */}
      <Spotlight
        $width={width}
        $height={height}
        $top={top}
        $left={left}
        $isActive={isActive}
        $isDarkMode={isDarkMode}
      />

      {/* Only show connecting line and dot when there's a label */}
      {spotlightState.label && showLabel && (
        <>
          <ConnectingLine
            $width={lineWidth}
            $height={lineHeight}
            $top={lineTop}
            $left={lineLeft}
            $isVertical={isVertical}
            $isActive={isActive}
            $isDarkMode={isDarkMode}
          />
          <GlowingDot
            $top={isVertical
              ? (positionMode === 'below' ? top + height - (dotSize / 2) : top - (dotSize / 2))
              : spotlightCenterY - (dotSize / 2)}
            $left={dotLeft}
            $isActive={isActive}
            $isDarkMode={isDarkMode}
          />
          <Label
            $top={labelTop}
            $left={labelLeft}
            $isActive={isActive}
            $isDarkMode={isDarkMode}
          >
            <button
              data-allow-demo-click="true"
              style={{
                position: "absolute",
                top: 4,
                right: 6,
                background: "none",
                border: "none",
                color: isDarkMode ? "#19fa6c" : "#316bff",
                fontSize: "1.2em",
                cursor: "pointer",
                opacity: 0.7,
                zIndex: SPOTLIGHT_Z + 3,
                pointerEvents: "auto"
              }}
              aria-label="Close"
              onClick={e => {
                e.stopPropagation();
                console.log('Close button clicked - hiding label');
                setShowLabel(false);
              }}
            >×</button>
            <Title $isDarkMode={isDarkMode}>Guided Demo:</Title>
            {spotlightState.label}
          </Label>
        </>
      )}

      {/* Close-button only if requested for this spotlight */}
      {spotlightState.showCloseButton && (
        <CloseButton
          $top={top}
          $left={left}
          $width={width}
          $isDarkMode={isDarkMode}
          data-allow-demo-click="true"
          onClick={(e) => {
            e.stopPropagation();
            setSpotlightState(null);
            setIsActive(false);
            if (spotlightState.onClose) spotlightState.onClose();
            else if (activeSpotlight?.onClose) activeSpotlight.onClose();
            else {
              onAction?.({ type: 'ADVANCE_DEMO' });
              onEffectComplete?.();
            }
          }}
        />
      )}
    </>,
    document.body
  );
};

export default SpotlightOverlay;
